import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FieldArray, FieldArrayRenderProps, FormikProps } from 'formik';
import Banner from '@cpg-its/ads.containers.banner';
import { esGenderOptions, GenderOptions } from '../../../../global/constants/lists';
import { PersonFields } from '../../models/form-models';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { FormField } from '../../../../global/components/form-element/form-field';
import { TcsmStates } from '../../../../root/tcsm-model';
import { DemographicsContext } from '../../../../global/models/demographics-model';
import { parseLov } from '../../../../global/helpers/form-data-helper';
import { PerformPersonSearchContext, PersonSearchContext } from '../../../arc/features/person-search/person-search-model';
import { handleOSCRRBlur } from '../../../arc/features/person-search/oscrr-helpers';
import { Person } from '../../../../global/models/person-model';
import { setDocToastHide, toggleDocumentUpload } from '../../features/personal-info/document-upload-slice';
import { shouldShowDocUpload } from '../../../../global/helpers/doc-upload-helpers';
import { getFormattedDate } from '../../../../global/helpers/date-helpers';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { WysiwygWithReactLink } from '../../../../global/components/wysiwyg-with-react-link';
import { personInfoSelfDescribeValue } from '../../../../global/validation-schemas/account-information-form-validation';
import { DocumentUploadState } from '../../features/personal-info/document-upload-models';
import { calculateAge } from '../../../../global/helpers/employee-helpers';
import { MyCPGMidYearProps } from '../../features/mycpg-midyear/models/mycpg-midyear-epi-props';
import { FieldAutomationIds } from '../../../../Global/constants/field-automation-ids';


interface BasicInfoProps {
	formikProps: FormikProps<any>;
	requiredSchema: PersonFields;
	renderKnownAs?: boolean;
	//showCleric?: boolean;
	renderDob?: boolean;
	renderTaxId?: boolean;
	currentPerson?: Person;
	docUpload?: boolean;
	dobWarningMessage?: JSX.Element;
	plusSixtyFive?: boolean;
	overrideDocUpload?: { taxId: boolean, dob: boolean, gender: boolean };
}

export function PersonInfoFields(props: BasicInfoProps) {
	const { formikProps, requiredSchema, renderDob, renderTaxId, currentPerson, docUpload, dobWarningMessage, plusSixtyFive, overrideDocUpload } = props;

	const app = useContext(DemographicsContext);
	const personSearchKey = useContext(PersonSearchContext);
	const performPersonSearch = useContext(PerformPersonSearchContext);
	const midYearSettings = useSelector<TcsmStates, MyCPGMidYearProps>(state => state?.epiProps?.myCpgMidyearSettingsState?.epiContent);
	const [isSixtyFive, setIsSixtyFive] = useState(false);

	const suffix = useSelector<TcsmStates, any>(state => state.lovProcState?.personLov?.suffix);
	const mappedSuffix = suffix ? parseLov(suffix) : [];

	const genderLov = useSelector<TcsmStates, any>(state => state.lovProcState?.personLov?.gender_identity);
	const parsedGenders = genderLov ? parseLov(genderLov) : [];

	const labelSetting = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps.labelSettingState.epiContent);
	const language = useSelector<TcsmStates, string>(state => state.globalization?.currentlanguage);
	//const isPersonClergy = useSelector<TcsmStates, string>(state => state?.personalInfo[app]?.currentPerson?.cleric_in_book);
	const dispatch = useDispatch();

	const documentUpoloadState = useSelector<TcsmStates, DocumentUploadState>(state => state.documentUpload[app]);

	useEffect(() => {
		if (currentPerson?.date_of_birth && plusSixtyFive) {
			const age = calculateAge(currentPerson?.date_of_birth);
			if (age >= 65) {
				setIsSixtyFive(true);
			}
		}
	}, [currentPerson, formikProps.values.date_of_birth]);

	const handleBlur = (e, fieldName: string) => {
		//Handle Datepicker passing in date object
		const value = e instanceof Date ? getFormattedDate(e?.toISOString()) : e?.target?.value;

		formikProps.setFieldTouched(fieldName, true);

		if (fieldName === 'date_of_birth' && plusSixtyFive){
			const age = calculateAge(e?.target?.value);
			if (age >= 65){
				setIsSixtyFive(true);
			}
			if (age < 65){
				setIsSixtyFive(false);
			}
		}

		if (performPersonSearch) {
			handleOSCRRBlur(dispatch, formikProps, personSearchKey, fieldName, value, !renderDob && !renderTaxId);
		}
	};

	const handleDocUpload = (e, fieldName: string, override = false) => {
		if (docUpload && !override) {
			dispatch(toggleDocumentUpload(shouldShowDocUpload(currentPerson, fieldName, e?.target?.value), app, documentUpoloadState?.showInternationalWarning));
			dispatch(setDocToastHide(false, app));
		}
	};

	const onGenderIdChange = (e, fieldArrayProps: FieldArrayRenderProps) => {
		const values = formikProps.values.gender_identity;
		const { value } = e.target;
		if (e.target.checked) {
			fieldArrayProps.push(value);
		} else {
			fieldArrayProps.remove(values.indexOf(value));
		}
	};

	const renderPlusSixtyFiveMessage = () => {
		if (renderDob && isSixtyFive){
			return (
				<ContentSpacer>
					<Banner banner={{
						type: 'info',
						label: <p className='h5-styled'>{midYearSettings?.plusSixtyFiveAlertTitle ?? labelSetting?.plusSixtyFiveAlertTitle} </p>,
						description: <WysiwygWithReactLink content={midYearSettings?.plusSixtyFiveAlertDescription ?? labelSetting?.plusSixtyFiveAlertTitle} />,
					}} />
				</ContentSpacer>
			);
		}
		return null;
	};

	return (
		<>

			<FormField
				name='first_name'
				label={labelSetting.firstNameLabel}
				component='input'
				touched={formikProps.touched}
				errors={formikProps.errors}
				required={requiredSchema.first_name}
				testId={FieldAutomationIds.employeeFirstName}

				//published={showCleric && isPersonClergy === 'Y'}
				tooltip={{
					title: labelSetting.tooltipMessage,
				}}
				onBlur={(e) => {
					handleBlur(e, 'first_name');
					handleDocUpload(e, 'first_name');
				}}
			/>

			<FormField
				name='middle_name'
				label={labelSetting.middleNameLabel}
				component='input'
				touched={formikProps.touched}
				errors={formikProps.errors}
				required={requiredSchema.middle_name}
				testId={FieldAutomationIds.employeeMiddleName}
				//published={showCleric && isPersonClergy === 'Y'}
				tooltip={{
					title: labelSetting.tooltipMessage,
				}}
				onBlur={(e) => handleDocUpload(e, 'middle_name')}
			/>

			<FormField
				name='last_name'
				label={labelSetting.lastNameLabel}
				component='input'
				touched={formikProps.touched}
				errors={formikProps.errors}
				required={requiredSchema.last_name}
				testId={FieldAutomationIds.employeeLastName}
				//published={showCleric && isPersonClergy === 'Y'}
				tooltip={{
					title: labelSetting.tooltipMessage,
				}}
				onBlur={(e) => {
					handleBlur(e, 'last_name');
					handleDocUpload(e, 'last_name');
				}}
			/>

			<FormField
				name='suffix'
				label={labelSetting.suffixLabel}
				component='input'
				options={mappedSuffix}
				errors={formikProps.errors}
				select
				required={requiredSchema.suffix}
				testId={FieldAutomationIds.suffixDropdown}
				//published={showCleric && isPersonClergy === 'Y'}
				autoWidth
				onBlur={(e) => handleDocUpload(e, 'suffix')}
			/>
			{
				props.renderKnownAs &&
				<FormField
					name='known_as'
					label={labelSetting.preferredLabel}
					component='input'
					touched={formikProps.touched}
					errors={formikProps.errors}
					required={requiredSchema.known_as}
					testId={FieldAutomationIds.preferredName}
				/>
			}
			<div className='form-item'>
				<ContentSpacer>
					<FormField
						name='gender'
						label={labelSetting.genderLabel}
						component='radioGroup'
						options={language === 'en' ? GenderOptions : esGenderOptions}
						touched={formikProps.touched}
						errors={formikProps.errors}
						tooltip={{
							title: labelSetting.genderTooltipMessage,
						}}
						required={requiredSchema.gender}
						onBlur={(e) => handleDocUpload(e, 'gender', overrideDocUpload.gender)}
						testId={FieldAutomationIds.genderTypeRadio}

					/>
				</ContentSpacer>
			</div>
			<FieldArray
				name='gender_identity'
				render={(fieldArrayProps) => (
					<>
						<ContentSpacer>
							<h5>{labelSetting.genderIdentityLabel}</h5>
							<WysiwygWithReactLink content={labelSetting.genderIdentityDescription} />
						</ContentSpacer>

						{
							parsedGenders.map(gender => {
								return (
									<FormField
										id={gender.value}
										key={gender.value}
										name='gender_identity'
										label={gender.text}
										component='checkBox'
										touched={formikProps.touched}
										errors={formikProps.errors}
										value={gender.value}
										checked={formikProps.values.gender_identity?.includes(gender.value)}
										checkBoxGroup
										onChange={(e) => onGenderIdChange(e, fieldArrayProps)}
										testId={FieldAutomationIds.genderIdentityOptions}


									/>
								);
							})
						}
					</>
				)}
			/>
			{
				formikProps.values.gender_identity?.includes(personInfoSelfDescribeValue) &&
				<FormField
					name='gender_identity_other'
					label={labelSetting.genderIdentitySelfDescribeLabel || 'Provide Description Below'}
					component='input'
					touched={formikProps.touched}
					errors={formikProps.errors}
					required
					testId={FieldAutomationIds.genderIdentityOptions}
				/>
			}

			{
				renderDob &&
				<FormField
					name='date_of_birth'
					label={labelSetting.dobLabel}
					component='datePicker'
					touched={formikProps.touched}
					errors={formikProps.errors}
					required={requiredSchema.date_of_birth}
					testId={FieldAutomationIds.dateOfBirth}
					onBlur={(e) => {
						handleBlur(e, 'date_of_birth');
						handleDocUpload(e, 'masked_date_of_birth', overrideDocUpload.dob);
					}}
					tooltip={{
						title: labelSetting.tooltipMessage,
					}}
					autoWidth
				/>
			}
			{
				renderPlusSixtyFiveMessage()
			}
			{
				dobWarningMessage 
			}
			{
				renderTaxId &&
				<FormField
					name='tax_id'
					label={labelSetting.ssnTinLabel}
					component='input'
					touched={formikProps.touched}
					errors={formikProps.errors}
					mask={[/[0-9]|X/, /\d|X/, /\d|X/, '-', /\d|X/, /\d|X/, '-', /\d/, /\d/, /\d/, /\d/]}
					autoWidth
					tooltip={{
						title: labelSetting.tooltipMessage,
					}}
					onBlur={(e) => {
						handleBlur(e, 'tax_id');
						handleDocUpload(e, 'masked_tax_id', overrideDocUpload.taxId);
					}}
					required={requiredSchema.tax_id}
					testId={FieldAutomationIds.taxId}
				/>
			}
			{/*<InputHelperTexts message='Maximum 9 characters' /> */}

		</>
	);
}

PersonInfoFields.defaultProps = {
	renderKnownAs: false,
	//showCleric: true,
	renderDob: true,
	renderTaxId: true,
	currentPerson: null,
	docUpload: false,
	dobWarningMessage: null,
	plusSixtyFive: false,
	overrideDocUpload: { taxId: false, dob: false, gender: false },
};
